import { ref, watch, nextTick } from 'vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import type { RouteLocationNormalized, RouteParams, LocationQueryRaw } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';

/**
 * Generates a guard for unsaved changes in forms.
 * @param submitCallback - Optional callback function to handle form submission
 * @returns Object - An object containing properties and methods to manage unsaved changes.
 */
export function useUnsavedChangesGuard(submitCallback?: () => void | Promise<void>) {
  const hasUnsavedChanges = ref(false);
  const hasUnsavedFormInput = ref(false);
  const unsavedChangesDialog = ref(false);
  // Store the complete route object instead of just the name
  const pendingNavigation = ref<RouteLocationNormalized | null>(null);
  const router = useRouter();
  const toast = useToast();

  /**
   * Checks if an object has any actual changes
   * @param obj - The object to check
   * @returns boolean - True if the object has data, false if it's empty
   */
  const hasActualChanges = (obj: any): boolean => {
    // Handle ref objects
    if (obj && obj.__v_isRef) {
      // For computed refs, check the actual value
      const value = obj.value;

      // If it's a boolean, return its value directly
      if (typeof value === 'boolean') {
        return value;
      }

      // Check if _rawValue exists and is not empty
      if (obj._rawValue && typeof obj._rawValue === 'object') {
        return Object.keys(obj._rawValue).length > 0;
      }

      // Handle arrays
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      // Handle objects
      if (value && typeof value === 'object') {
        return Object.keys(value).length > 0;
      }

      // If it's a primitive value ref, check the value directly
      return !!value;
    }

    // Handle regular objects
    if (obj && typeof obj === 'object') {
      return Object.keys(obj).length > 0;
    }

    // For primitive values
    return !!obj;
  };

  /**
   * Tracks changes in the specified fields and sets the unsaved changes flag.
   * @param fields - The fields to track for changes (can be a Ref, an Array, or a single field).
   * @param callback - Optional callback function to execute when a change is detected.
   */
  const trackChanges = (fields: any, callback?: () => void) => {
    // Handle array of fields - create separate watch for each field
    if (Array.isArray(fields)) {
      fields.forEach((field, index) => {
        watch(field, (newValue) => {

          hasUnsavedChanges.value = hasActualChanges(newValue);

          if (callback) callback();
        }, { deep: true });
      });
    } else {
      // Handle single field
      watch(fields, (newValue) => {

        hasUnsavedChanges.value = hasActualChanges(newValue);

        if (callback) callback();
      }, { deep: true });
    }
  };

  /**
   * Tracks changes in a specific field and sets the unsaved changes flag.
   * @param field - The field to track for changes.
   * @param callback - Optional callback function to execute when a change is detected.
   */
  const trackInputChanges = (field: any, callback?: () => void) => {
    watch(field, (newValue) => {
      if (newValue && newValue.trim && newValue.trim() !== '') {
        hasUnsavedChanges.value = true;
        hasUnsavedFormInput.value = true;
      } else {
        hasUnsavedFormInput.value = false;
      }
      if (callback) callback();
    });
  };

  /**
   * Handles the beforeunload event to prompt the user if there are unsaved changes.
   * @param e - The beforeunload event object.
   */
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (hasUnsavedChanges.value) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  /**
   * Sets up the route guard to prompt the user if there are unsaved changes.
   * @returns Function - A function to remove the event listener.
   */
  const setupGuard = () => {
    onBeforeRouteLeave((to, from, next) => {
      if (hasUnsavedChanges.value) {
        // Store the complete target route
        pendingNavigation.value = to;
        unsavedChangesDialog.value = true;
        next(false);
      } else {
        next();
      }
    });

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  };

  /**
   * Proceeds with the navigation after confirming unsaved changes.
   */
  const proceedWithNavigation = () => {
    // Reset state first
    unsavedChangesDialog.value = false;
    resetUnsavedChanges();

    // Save the route locally as pendingNavigation might be reset by nextTick
    const targetRoute = pendingNavigation.value;

    nextTick(() => {
      // Check if we have a pending navigation
      if (targetRoute) {
        router.push(targetRoute);
        // Clear after navigation
        pendingNavigation.value = null;
      }
    });
  };

  /**
   * Cancels the navigation and resets the unsaved changes dialog.
   */
  const cancelNavigation = () => {
    unsavedChangesDialog.value = false;
    pendingNavigation.value = null;
  };

  /**
   * Saves changes, calls the submit callback if provided, and then cancels navigation
   */
  const saveChanges = async () => {
    // Reset state first
    unsavedChangesDialog.value = false;
    // Save the route locally as pendingNavigation might be reset by nextTick
    const targetRoute = pendingNavigation.value;
    // Call the submit callback if provided
    if (submitCallback && typeof submitCallback === 'function') {
      try {
        await submitCallback();
        resetUnsavedChanges();
        nextTick(() => {
          // Check if we have a pending navigation
          if (targetRoute) {
            router.push(targetRoute);
            // Clear after navigation
            pendingNavigation.value = null;
          }
        });
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: useI18n().t('dialogs.unsavedChanges.toast.saveError'),
          detail: useI18n().t('dialogs.unsavedChanges.toast.callbackError'),
          life: 3000
        });
        return cancelNavigation();
      }
    }
    else {
      toast.add({
        severity: 'error',
        summary: useI18n().t('dialogs.unsavedChanges.toast.saveError'),
        detail: useI18n().t('dialogs.unsavedChanges.toast.callbackError'),
        life: 3000
      });
      return cancelNavigation();
    }
  };

  /**
   * Resets the unsaved changes flags.
   */
  const resetUnsavedChanges = () => {
    hasUnsavedChanges.value = false;
    hasUnsavedFormInput.value = false;
  };

  /**
   * Navigates to a specified route, prompting for unsaved changes if necessary.
   * @param name - The name of the route to navigate to.
   * @param params - Optional route parameters.
   * @param query - Optional query parameters.
   * @param hash - Optional URL hash.
   */
  const navigateTo = (
    name: string, 
    params?: RouteParams, 
    query?: LocationQueryRaw, 
    hash?: string
  ) => {
    if (hasUnsavedChanges.value) {
      pendingNavigation.value = router.resolve({ 
        name, 
        params, 
        query, 
        hash 
      }) as RouteLocationNormalized;
      unsavedChangesDialog.value = true;
    } else {
      router.push({ 
        name, 
        params, 
        query, 
        hash 
      });
    }
  };

  // Return the properties and methods for use in components
  return {
    hasUnsavedChanges,
    hasUnsavedFormInput,
    unsavedChangesDialog,
    pendingNavigation,
    trackChanges,
    trackInputChanges,
    setupGuard,
    proceedWithNavigation,
    cancelNavigation,
    saveChanges,
    resetUnsavedChanges,
    handleBeforeUnload,
    navigateTo
  };
}